import type { ISummaryData } from 'containers/KitWorkspaceStorage/types';
import type { IPdfRenderPayload } from 'api/types';
import { readBool } from 'utils/readBool';
import { readBrandAccent, resolveLogoUrl } from './pdfBranding';

/**
 * The server-rendered PDF ships per account, as the kit builder and the group
 * flow do: a collection with both toggles off has not been signed off yet and
 * keeps the PDF its printer has always produced. Sending no payload is what
 * tells the backend to render that html instead.
 */
export const isServerPdfEnabled = (): boolean => (
  readBool('variables.showManageKits') || readBool('variables.showAssemblyGroup')
);

// Each tenant types its translate function to its own key union, and the keys
// read here are optional per tenant (only @danfloss declares companyName), so
// the key type stays loose on purpose.
type TranslateFn = (key: any) => string;

// i18next returns the key itself when a variable is unset, so a value equal to
// its key means "not configured for this tenant".
const readText = (t: TranslateFn, key: string): string | undefined => {
  const value = t(key);
  return value && value !== key ? value : undefined;
};

/**
 * Inputs for the server-rendered summary PDF: the configured assembly plus the
 * tenant branding the legacy printer used to bake into its html. Sent alongside
 * the html so a backend without the new rendering still works.
 */
export const buildPdfRenderPayload = (
  summaryData: ISummaryData,
  t: TranslateFn,
): IPdfRenderPayload => ({
  summaryData,
  accentColor: readBrandAccent(),
  logoUrl: resolveLogoUrl(t('variables.pdf.logo')),
  branding: {
    title: readText(t, 'texts.pdf.title'),
    companyName: readText(t, 'variables.companyName'),
    tos: readText(t, 'texts.pdf.tos'),
  },
});
